import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi, KnowledgeBaseConfig, TenantProfile, TaskHistory } from '../services/knowledgeBaseApi';
import AIConfigCreator from '../components/knowledge/AIConfigCreator';
import AIConfigEditor from '../components/knowledge/AIConfigEditor';
import DomainFilter from '../components/knowledge/DomainFilter';
import DomainRuleCreator from '../components/knowledge/DomainRuleCreator';
import InfoButton from '../components/knowledge/InfoButton';
import '../styles/KnowledgeBase.css';

type TabType = 'frameworks' | 'rules' | 'checks' | 'profile' | 'history';
type ConfigType = 'task' | 'domain' | 'safety';

const KnowledgeBase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('frameworks');
  const [configs, setConfigs] = useState<Record<string, KnowledgeBaseConfig[]>>({});
  const [tenantProfile, setTenantProfile] = useState<TenantProfile | null>(null);
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<KnowledgeBaseConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  // Domain filter state for task frameworks
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  // Domain filter state for domain rules
  const [selectedRuleDomains, setSelectedRuleDomains] = useState<string[]>([]);
  // Profile edit state
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<any>(null);
  // Task History search and filter state
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskFilterStatus, setTaskFilterStatus] = useState<'all' | 'excellent' | 'good' | 'needs-work'>('all');
  // AI creation state
  const [showAICreator, setShowAICreator] = useState(false);
  const [aiCreatorType, setAICreatorType] = useState<'task' | 'domain' | 'safety'>('task');
  const [showDomainRuleCreator, setShowDomainRuleCreator] = useState(false);
  const [aiEditMode, setAIEditMode] = useState(false);
  const [aiEditRequest, setAIEditRequest] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all configurations
      const configsData = await knowledgeBaseApi.getAllConfigs();
      setConfigs(configsData);

      // Load tenant profile
      const profileData = await knowledgeBaseApi.getTenantProfile();
      setTenantProfile(profileData);

      // Load task history
      const historyData = await knowledgeBaseApi.getTaskHistory();
      setTaskHistory(historyData);
    } catch (err: any) {
      console.error('Error loading knowledge base data:', err);
      const errorMessage = err.message || 'Failed to load knowledge base data';
      
      // Check if it's a 404 (no data) - try onboarding
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        try {
          setError('No knowledge base found. Initializing...');
          await knowledgeBaseApi.onboardTenant();
          // Retry loading after onboarding
          await loadData();
          setError(null); // Clear error after successful onboarding
        } catch (onboardErr: any) {
          console.error('Onboarding failed:', onboardErr);
          setError(`Failed to initialize knowledge base: ${onboardErr.message || 'Unknown error'}`);
        }
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSelect = async (config: KnowledgeBaseConfig) => {
    setSelectedConfig(config);
    setSuccessMessage(null);
  };

  // Removed legacy handlers - now using AI-powered editing

  const renderConfigList = (configType: ConfigType) => {
    const configList = configs[configType] || [];
    
    // Filter configs based on search query and domain
    let filteredConfigs = configList;
    
    // Apply domain filter for task frameworks
    if (configType === 'task' && selectedDomains.length > 0) {
      filteredConfigs = filteredConfigs.filter(config => {
        const domains = config.data?.domains || config.config_data?.domains || [];
        return domains.some((d: string) => selectedDomains.includes(d));
      });
    }
    
    // Apply domain filter for domain rules (single domain per rule)
    if (configType === 'domain' && selectedRuleDomains.length > 0) {
      filteredConfigs = filteredConfigs.filter(config => {
        // Domain rules use config_name as the domain identifier
        return config.config_name && selectedRuleDomains.includes(config.config_name);
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      filteredConfigs = filteredConfigs.filter(config => {
        const configName = config.name?.toLowerCase() || '';
        const searchLower = searchQuery.toLowerCase();
        // Search in name and also in the config data if available
        const configDataStr = JSON.stringify(config.data || config.config_data || {}).toLowerCase();
        return configName.includes(searchLower) || configDataStr.includes(searchLower);
      });
    }
    
    // Note: Removed domain grouping since we're using multi-select filter
    
    return (
      <div className="config-list">
        <div className="config-list-header">
          <h3>
            {configType.replace('_', ' ').toUpperCase()}
            {configType === 'task' && (
              <InfoButton 
                title="Task Frameworks"
                content={{
                  what: "Task frameworks are complete, standardized procedures for common accounting tasks like bank reconciliation, AR aging, or expense categorization. They contain step-by-step requirements, validation rules, and expected outputs.",
                  how: "When you start a workflow, AI first attempts to identify if it matches a known task framework. If it does, the entire framework is applied to ensure consistent, comprehensive execution.",
                  precedence: "Task frameworks have second priority after user instructions. They override domain rules and company defaults when matched. Think of them as 'complete recipes' that define the entire workflow."
                }}
              />
            )}
            {configType === 'domain' && (
              <InfoButton 
                title="Domain Rules"
                content={{
                  what: "Domain rules are specific accounting principles and business logic that apply across multiple tasks (e.g., revenue recognition policies, depreciation methods, tax calculations).",
                  how: "Domain rules provide specialized accounting logic for specific areas. They're applied when working within that domain, either standalone or referenced by task frameworks.",
                  precedence: "Domain rules have third priority. They override company defaults but are overridden by task frameworks and user instructions. They apply when: (1) no task framework matches, (2) a task framework references them, or (3) doing domain-specific analysis."
                }}
              />
            )}
            {configType === 'safety' && (
              <InfoButton 
                title="Safety Checks"
                content={{
                  what: "Safety checks are validation rules and controls that prevent errors, catch anomalies, and ensure data integrity across all accounting processes. They include data completeness, accuracy, consistency, and reasonableness checks.",
                  how: "Safety checks are included in the AI context to guide validation logic. They provide standard checks like null validation, date ranges, duplicate detection, and balance verification that the AI incorporates into generated scripts.",
                  precedence: "Safety checks are guidelines rather than mandatory overrides. They're provided to the AI as best practices to include in scripts, but user instructions can override them if needed (e.g., allowing negative values when normally restricted)."
                }}
              />
            )}
          </h3>
          {(configType === 'task' || configType === 'domain' || configType === 'safety') && (
            <button 
              className="btn-create-ai"
              onClick={() => {
                if (configType === 'domain') {
                  setShowDomainRuleCreator(true);
                } else {
                  setAICreatorType(configType);
                  setShowAICreator(true);
                }
              }}
              title={`Create new ${configType === 'task' ? 'framework' : configType === 'domain' ? 'rules' : 'safety check'} with AI`}
            >
              + AI Create {configType === 'task' ? 'Framework' : configType === 'domain' ? 'Rules' : 'Safety Check'}
            </button>
          )}
        </div>
        
        {/* Search bar and domain filter - skip for safety checks since there's only one */}
        {configType !== 'safety' && (
          <div className="config-search-container">
            {configType === 'task' && (
              <div style={{ marginBottom: '12px' }}>
                <DomainFilter
                  configs={configList}
                  selectedDomains={selectedDomains}
                  onDomainsChange={setSelectedDomains}
                  itemType="frameworks"
                />
              </div>
            )}
            {configType === 'domain' && (
              <div style={{ marginBottom: '12px' }}>
                <DomainFilter
                  configs={configList}
                  selectedDomains={selectedRuleDomains}
                  onDomainsChange={setSelectedRuleDomains}
                  itemType="rules"
                />
              </div>
            )}
            <div className="config-search-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="config-search-input"
                placeholder={`Search ${configType === 'task' ? 'frameworks' : configType === 'domain' ? 'rules' : 'checks'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  className="search-clear-btn"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-count">
                {filteredConfigs.length} of {configList.length} {configType === 'task' ? 'frameworks' : configType === 'domain' ? 'rules' : 'checks'}
              </div>
            )}
          </div>
        )}
        
        <div className="config-items">
          {filteredConfigs.length === 0 && searchQuery ? (
            <div className="no-search-results">
              No {configType === 'task' ? 'frameworks' : configType === 'domain' ? 'rules' : 'checks'} found matching "{searchQuery}"
            </div>
          ) : (
            filteredConfigs.map((config) => {
              // Highlight matching text
              const configName = config.name?.replace('_', ' ') || 'Unnamed';
              const highlightedName = searchQuery.trim() 
                ? configName.replace(
                    new RegExp(`(${searchQuery})`, 'gi'),
                    '<mark>$1</mark>'
                  )
                : configName;
              
              return (
                <div
                  key={`${config.type}-${config.name}`}
                  className={`config-item ${selectedConfig?.name === config.name ? 'selected' : ''} ${config.is_system ? 'system-config' : ''}`}
                  onClick={() => handleConfigSelect(config)}
                >
                  <div className="config-name">
                    {config.is_system && <span className="system-icon" title="System framework - cannot be renamed">🔒</span>}
                    <span dangerouslySetInnerHTML={{ __html: highlightedName }} />
                  </div>
                  <div className="config-meta">
                    <span>v{config.version !== undefined ? config.version : 0}</span>
                    {config.modified_at && (
                      <span>Modified: {new Date(config.modified_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderConfigDetails = () => {
    if (!selectedConfig) {
      return <div className="config-details-empty">Select a configuration to view details</div>;
    }

    const configType = selectedConfig.type || selectedConfig.config_type || 'task';
    const configName = selectedConfig.name || selectedConfig.config_name || 'unnamed';
    const configData = selectedConfig.data || selectedConfig.config_data || selectedConfig;

    return (
      <div className="config-details">
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
        
        <AIConfigEditor
          config={configData}
          configType={configType}
          configName={configName}
          onConfigUpdated={async (updatedConfig) => {
            try {
              // Check if the name has changed
              const nameChanged = updatedConfig.name && updatedConfig.name !== configName;
              
              // Prevent renaming system frameworks
              if (nameChanged && selectedConfig?.is_system) {
                console.error('Cannot rename system framework');
                return;
              }
              
              const newConfigName = nameChanged ? 
                updatedConfig.name.toLowerCase().replace(/\s+/g, '_') : 
                configName;
              
              // Save the updated config
              const result = await knowledgeBaseApi.updateConfig(
                configType,
                configName,
                { 
                  config_data: updatedConfig,
                  ...(nameChanged && { config_name: newConfigName })
                }
              );
              
              // Update local state
              setSelectedConfig({
                ...selectedConfig,
                data: updatedConfig,
                config_data: updatedConfig,
                ...(nameChanged && { 
                  name: updatedConfig.name,
                  config_name: newConfigName 
                })
              });
              
              // Update the configs list
              const updatedConfigs = { ...configs };
              if (updatedConfigs[configType]) {
                const index = updatedConfigs[configType].findIndex(
                  c => (c.name || c.config_name) === configName
                );
                if (index !== -1) {
                  updatedConfigs[configType][index] = {
                    ...updatedConfigs[configType][index],
                    data: updatedConfig,
                    config_data: updatedConfig,
                    ...(nameChanged && { 
                      name: updatedConfig.name,
                      config_name: newConfigName 
                    })
                  };
                }
              }
              setConfigs(updatedConfigs);
              
              setSuccessMessage(nameChanged ? 
                'Configuration name and data updated successfully' : 
                'Configuration updated successfully'
              );
              setTimeout(() => setSuccessMessage(null), 3000);
            } catch (err: any) {
              setError(err.message || 'Failed to update configuration');
            }
          }}
        />
      </div>
    );
  };

  const handleEditClick = () => {
    setEditedProfile({ ...tenantProfile });
    setProfileEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditedProfile(null);
    setProfileEditMode(false);
    setError(null);
  };

  const handleSaveProfile = async () => {
    if (!editedProfile) return;
    
    try {
      const result = await knowledgeBaseApi.updateTenantProfile(editedProfile);
      setTenantProfile(result.profile);
      setProfileEditMode(false);
      setEditedProfile(null);
      setSuccessMessage('Profile updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    }
  };

  const updateProfileField = (field: string, value: any) => {
    if (editedProfile) {
      setEditedProfile({ ...editedProfile, [field]: value });
    }
  };

  const renderTenantProfile = () => {
    if (!tenantProfile) {
      return <div>Loading profile...</div>;
    }

    const currentProfile = profileEditMode ? editedProfile : tenantProfile;

    return (
      <div className="tenant-profile">
        <div className="profile-header">
          <h3>Company Profile</h3>
          <div className="profile-actions">
            {!profileEditMode ? (
              <button className="btn-edit" onClick={handleEditClick}>Edit Profile</button>
            ) : (
              <>
                <button className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                <button className="btn-save" onClick={handleSaveProfile}>Save Changes</button>
              </>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        <div className="profile-sections">
          {/* Basic Information */}
          <div className="profile-section">
            <h4>Basic Information</h4>
            <div className="profile-fields">
              <div className="profile-field">
                <label>Company Name</label>
                <input
                  type="text"
                  value={currentProfile?.company_name || ''}
                  onChange={(e) => updateProfileField('company_name', e.target.value)}
                  disabled={!profileEditMode}
                  placeholder="Enter company name"
                />
              </div>
              <div className="profile-field">
                <label>Industry</label>
                <input
                  type="text"
                  value={currentProfile?.industry || ''}
                  onChange={(e) => updateProfileField('industry', e.target.value)}
                  disabled={!profileEditMode}
                  placeholder="e.g., Technology, Healthcare, Manufacturing"
                />
                <span className="field-help">Helps AI understand your business context</span>
              </div>
              <div className="profile-field">
                <label>Company Size</label>
                <select
                  value={currentProfile?.company_size || ''}
                  onChange={(e) => updateProfileField('company_size', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select size...</option>
                  <option value="startup">Startup (1-10 employees)</option>
                  <option value="small">Small (11-50 employees)</option>
                  <option value="medium">Medium (51-200 employees)</option>
                  <option value="large">Large (201-1000 employees)</option>
                  <option value="enterprise">Enterprise (1000+ employees)</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Annual Revenue Range</label>
                <select
                  value={currentProfile?.revenue_range || ''}
                  onChange={(e) => updateProfileField('revenue_range', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select range...</option>
                  <option value="0-1M">$0 - $1M</option>
                  <option value="1M-10M">$1M - $10M</option>
                  <option value="10M-50M">$10M - $50M</option>
                  <option value="50M-100M">$50M - $100M</option>
                  <option value="100M-500M">$100M - $500M</option>
                  <option value="500M+">$500M+</option>
                </select>
              </div>
            </div>
          </div>

          {/* Accounting Settings */}
          <div className="profile-section">
            <h4>Accounting Settings</h4>
            <div className="profile-fields">
              <div className="profile-field">
                <label>Accounting Basis</label>
                <select
                  value={currentProfile?.accounting_basis || 'accrual'}
                  onChange={(e) => updateProfileField('accounting_basis', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="accrual">Accrual</option>
                  <option value="cash">Cash</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Fiscal Year End</label>
                <input
                  type="text"
                  value={currentProfile?.fiscal_year_end || ''}
                  onChange={(e) => updateProfileField('fiscal_year_end', e.target.value)}
                  disabled={!profileEditMode}
                  placeholder="e.g., December 31, June 30"
                />
              </div>
              <div className="profile-field">
                <label>Primary Currency</label>
                <select
                  value={currentProfile?.primary_currency || 'USD'}
                  onChange={(e) => updateProfileField('primary_currency', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Materiality Threshold</label>
                <input
                  type="number"
                  value={currentProfile?.materiality_threshold || ''}
                  onChange={(e) => updateProfileField('materiality_threshold', parseFloat(e.target.value))}
                  disabled={!profileEditMode}
                  placeholder="e.g., 5000"
                />
                <span className="field-help">Amounts below this are considered immaterial</span>
              </div>
            </div>
          </div>

          {/* Systems & Integration */}
          <div className="profile-section">
            <h4>Systems & Integration</h4>
            <div className="profile-fields">
              <div className="profile-field">
                <label>ERP System</label>
                <select
                  value={currentProfile?.erp_system || ''}
                  onChange={(e) => updateProfileField('erp_system', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select ERP...</option>
                  <option value="quickbooks">QuickBooks</option>
                  <option value="xero">Xero</option>
                  <option value="netsuite">NetSuite</option>
                  <option value="sap">SAP</option>
                  <option value="oracle">Oracle</option>
                  <option value="dynamics">Microsoft Dynamics</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Bank Connection Type</label>
                <select
                  value={currentProfile?.bank_connection || ''}
                  onChange={(e) => updateProfileField('bank_connection', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select type...</option>
                  <option value="manual">Manual Import (CSV/Excel)</option>
                  <option value="api">Bank API Integration</option>
                  <option value="plaid">Plaid</option>
                  <option value="yodlee">Yodlee</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Primary Banks (comma-separated)</label>
                <input
                  type="text"
                  value={currentProfile?.primary_banks || ''}
                  onChange={(e) => updateProfileField('primary_banks', e.target.value)}
                  disabled={!profileEditMode}
                  placeholder="e.g., Chase, Wells Fargo, Bank of America"
                />
              </div>
            </div>
          </div>

          {/* Compliance & Reporting */}
          <div className="profile-section">
            <h4>Compliance & Reporting</h4>
            <div className="profile-fields">
              <div className="profile-field">
                <label>Reporting Standards</label>
                <select
                  value={currentProfile?.reporting_standards || ''}
                  onChange={(e) => updateProfileField('reporting_standards', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select standard...</option>
                  <option value="gaap">US GAAP</option>
                  <option value="ifrs">IFRS</option>
                  <option value="cash">Cash Basis</option>
                  <option value="tax">Tax Basis</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Tax Structure</label>
                <select
                  value={currentProfile?.tax_structure || ''}
                  onChange={(e) => updateProfileField('tax_structure', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select structure...</option>
                  <option value="c-corp">C Corporation</option>
                  <option value="s-corp">S Corporation</option>
                  <option value="llc">LLC</option>
                  <option value="partnership">Partnership</option>
                  <option value="sole-prop">Sole Proprietorship</option>
                  <option value="non-profit">Non-Profit</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Audit Requirements</label>
                <select
                  value={currentProfile?.audit_requirements || ''}
                  onChange={(e) => updateProfileField('audit_requirements', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select requirement...</option>
                  <option value="none">No audit required</option>
                  <option value="review">Review engagement</option>
                  <option value="compilation">Compilation</option>
                  <option value="audit">Full audit</option>
                  <option value="sox">SOX compliance audit</option>
                </select>
              </div>
            </div>
          </div>

          {/* Workflow Preferences */}
          <div className="profile-section">
            <h4>Workflow Preferences</h4>
            <div className="profile-fields">
              <div className="profile-field">
                <label>Month-End Close Timeline</label>
                <select
                  value={currentProfile?.close_timeline || ''}
                  onChange={(e) => updateProfileField('close_timeline', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select timeline...</option>
                  <option value="3-days">3 business days</option>
                  <option value="5-days">5 business days</option>
                  <option value="10-days">10 business days</option>
                  <option value="15-days">15 business days</option>
                  <option value="monthly">End of following month</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Approval Levels Required</label>
                <select
                  value={currentProfile?.approval_levels || ''}
                  onChange={(e) => updateProfileField('approval_levels', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select levels...</option>
                  <option value="none">No approvals needed</option>
                  <option value="single">Single approval</option>
                  <option value="dual">Dual approval</option>
                  <option value="hierarchical">Hierarchical (multiple levels)</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Preferred Output Format</label>
                <select
                  value={currentProfile?.output_format || 'excel'}
                  onChange={(e) => updateProfileField('output_format', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="excel">Excel with formatting</option>
                  <option value="csv">CSV (simple)</option>
                  <option value="pdf">PDF reports</option>
                  <option value="json">JSON (for integration)</option>
                </select>
              </div>
              <div className="profile-field">
                <label>Rounding Preference</label>
                <select
                  value={currentProfile?.rounding_preference || ''}
                  onChange={(e) => updateProfileField('rounding_preference', e.target.value)}
                  disabled={!profileEditMode}
                >
                  <option value="">Select preference...</option>
                  <option value="exact">Exact (no rounding)</option>
                  <option value="cents">To cents (0.01)</option>
                  <option value="dollars">To dollars (1.00)</option>
                  <option value="thousands">To thousands (1,000)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaskHistory = () => {
    // Helper function to calculate success rate
    const calculateSuccessRate = (task: any): number => {
      // Handle both old and new data structures
      const successCount = task.success_count ?? Math.max(0, (task.execution_count || 0) - (task.failure_count || 0));
      const failureCount = task.failure_count ?? 0;
      const total = successCount + failureCount;
      if (total === 0) return task.execution_count > 0 ? 100 : 0; // If we only have execution_count, assume success
      return Math.round((successCount / total) * 100);
    };

    // Map backend data structure to expected frontend structure
    const mappedHistory = taskHistory.map(task => ({
      ...task,
      success_count: task.success_count ?? Math.max(0, (task.execution_count || 0) - (task.failure_count || 0)),
      failure_count: task.failure_count ?? 0,
      id: task.id || `${task.task_type}_${Date.now()}`,
      modified_at: task.last_executed || task.modified_at
    }));

    // Apply search and filters
    const filteredHistory = mappedHistory.filter(task => {
      // Search filter
      if (taskSearchTerm && !task.task_type.toLowerCase().includes(taskSearchTerm.toLowerCase())) {
        return false;
      }
      
      // Status filter
      if (taskFilterStatus !== 'all') {
        const successRate = calculateSuccessRate(task);
        if (taskFilterStatus === 'excellent' && successRate < 90) return false;
        if (taskFilterStatus === 'good' && (successRate < 70 || successRate >= 90)) return false;
        if (taskFilterStatus === 'needs-work' && successRate >= 70) return false;
      }
      
      return true;
    });

    // Helper function to convert technical patterns to human-readable insights
    const getHumanReadablePattern = (pattern: any): string => {
      if (typeof pattern === 'string') return pattern;
      
      if (pattern.type === 'date_handling' && pattern.pattern) {
        return `📅 Date format detected: ${pattern.pattern}`;
      }
      if (pattern.type === 'matching_logic' && pattern.pattern) {
        return `🔗 Successfully matched records using: ${pattern.pattern}`;
      }
      if (pattern.code_snippet) {
        const snippet = pattern.code_snippet;
        if (snippet.includes('datetime')) {
          return `📅 Date parsing: ${pattern.pattern || 'Custom date format detected'}`;
        }
        if (snippet.includes('merge')) {
          return `🔗 Data merge: ${pattern.pattern || 'Join operation on key columns'}`;
        }
        if (snippet.includes('groupby')) {
          return `📊 Grouping: Data aggregated by specific columns`;
        }
        if (snippet.includes('pivot')) {
          return `📈 Pivot table: Created summary view of data`;
        }
      }
      
      // Try to extract meaningful text from the pattern
      if (pattern.pattern) return `✓ ${pattern.pattern}`;
      if (pattern.description) return `✓ ${pattern.description}`;
      
      // Fallback - try to make it somewhat readable
      const str = JSON.stringify(pattern);
      if (str.length > 100) return '✓ Complex pattern learned';
      return `✓ Pattern: ${str.replace(/[{}"]/g, '')}`;
    };

    const getHumanReadableIssue = (issue: any): string => {
      if (typeof issue === 'string') return issue;
      
      if (issue.type === 'missing_column') {
        return `⚠️ Missing column: ${issue.column || 'Required column not found'}`;
      }
      if (issue.type === 'date_parse_error') {
        return `⚠️ Date format issue: Could not parse dates in expected format`;
      }
      if (issue.type === 'duplicate_detection') {
        return `⚠️ Duplicates found: Multiple records with same identifier`;
      }
      if (issue.error) {
        return `⚠️ Error: ${issue.error}`;
      }
      if (issue.message) {
        return `⚠️ ${issue.message}`;
      }
      
      const str = JSON.stringify(issue);
      if (str.length > 100) return '⚠️ Complex issue encountered';
      return `⚠️ Issue: ${str.replace(/[{}"]/g, '')}`;
    };

    const getStatusColor = (successRate: number): string => {
      if (successRate >= 90) return 'success';
      if (successRate >= 70) return 'warning';
      return 'error';
    };

    const getTaskIcon = (taskType: string): string => {
      const icons: Record<string, string> = {
        'bank_reconciliation': '🏦',
        'expense_categorization': '📊',
        'journal_entry': '📝',
        'ar_aging': '📈',
        'ap_aging': '📉',
        'financial_consolidation': '💼',
        'tax_calculation': '🧮',
        'payroll': '💰',
        'inventory': '📦',
        'revenue_recognition': '💵'
      };
      return icons[taskType] || '📋';
    };

    // Group tasks by success rate for summary (use all tasks, not filtered)
    const summary = {
      excellent: mappedHistory.filter(t => calculateSuccessRate(t) >= 90).length,
      good: mappedHistory.filter(t => calculateSuccessRate(t) >= 70 && calculateSuccessRate(t) < 90).length,
      needsWork: mappedHistory.filter(t => calculateSuccessRate(t) < 70).length,
      total: mappedHistory.length
    };

    return (
      <div className="task-history-container">
        {/* Header with summary */}
        <div className="history-header-section">
          <h3>Task Execution History</h3>
          
          {/* Search and Filter Bar */}
          {mappedHistory.length > 0 && (
            <div className="history-controls">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="🔍 Search tasks..."
                  value={taskSearchTerm}
                  onChange={(e) => setTaskSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${taskFilterStatus === 'all' ? 'active' : ''}`}
                  onClick={() => setTaskFilterStatus('all')}
                >
                  All ({summary.total})
                </button>
                <button
                  className={`filter-btn excellent ${taskFilterStatus === 'excellent' ? 'active' : ''}`}
                  onClick={() => setTaskFilterStatus('excellent')}
                >
                  Excellent ({summary.excellent})
                </button>
                <button
                  className={`filter-btn good ${taskFilterStatus === 'good' ? 'active' : ''}`}
                  onClick={() => setTaskFilterStatus('good')}
                >
                  Good ({summary.good})
                </button>
                <button
                  className={`filter-btn needs-work ${taskFilterStatus === 'needs-work' ? 'active' : ''}`}
                  onClick={() => setTaskFilterStatus('needs-work')}
                >
                  Needs Work ({summary.needsWork})
                </button>
              </div>
            </div>
          )}
          
          {mappedHistory.length > 0 && (
            <div className="history-summary">
              <div className="summary-card excellent">
                <span className="summary-count">{summary.excellent}</span>
                <span className="summary-label">Excellent (90%+)</span>
              </div>
              <div className="summary-card good">
                <span className="summary-count">{summary.good}</span>
                <span className="summary-label">Good (70-89%)</span>
              </div>
              <div className="summary-card needs-work">
                <span className="summary-count">{summary.needsWork}</span>
                <span className="summary-label">Needs Work (&lt;70%)</span>
              </div>
            </div>
          )}
        </div>

        {/* Show different messages based on context */}
        {mappedHistory.length === 0 ? (
          <div className="no-history">
            <div className="no-history-icon">📊</div>
            <h4>No Task History Yet</h4>
            <p>As you run workflows, Assertion will track patterns and learn from each execution.</p>
            <p>This helps improve accuracy and automation over time.</p>
          </div>
        ) : (
          filteredHistory.length === 0 ? (
            <div className="no-history">
              <div className="no-history-icon">🔍</div>
              <h4>No Matching Tasks</h4>
              <p>Try adjusting your search or filter criteria</p>
              <button 
                onClick={() => { setTaskSearchTerm(''); setTaskFilterStatus('all'); }}
                className="reset-filters-btn"
              >
                Clear Filters
              </button>
            </div>
          ) : (
          <div className="history-cards">
            {filteredHistory.map((task) => {
              const successRate = calculateSuccessRate(task);
              const statusColor = getStatusColor(successRate);
              const successCount = task.success_count ?? Math.max(0, (task.execution_count || 0) - (task.failure_count || 0));
              const failureCount = task.failure_count ?? 0;
              const totalRuns = task.execution_count || (successCount + failureCount);

              return (
                <div key={task.id} className={`history-card ${statusColor}`}>
                  {/* Card Header */}
                  <div className="card-header">
                    <div className="task-info">
                      <span className="task-icon">{getTaskIcon(task.task_type)}</span>
                      <div>
                        <h4 className="task-name">
                          {task.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h4>
                        <span className="task-runs">{totalRuns} total runs</span>
                      </div>
                    </div>
                    <div className="success-indicator">
                      <div className="success-rate-circle">
                        <span className="rate-number">{successRate}%</span>
                        <span className="rate-label">Success</span>
                      </div>
                    </div>
                  </div>

                  {/* Stats Bar */}
                  <div className="stats-bar">
                    <div className="stat-item">
                      <span className="stat-label">Successful</span>
                      <span className="stat-value success">{successCount}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Failed</span>
                      <span className="stat-value error">{failureCount}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Last Run</span>
                      <span className="stat-value">
                        {(task.last_executed || task.modified_at) ? new Date(task.last_executed || task.modified_at || '').toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Insights Section */}
                  {((task.learned_patterns?.length ?? 0) > 0 || (task.previous_issues?.length ?? 0) > 0) && (
                    <div className="insights-section">
                      {(task.learned_patterns?.length ?? 0) > 0 && (
                        <div className="insight-group">
                          <h5>✨ What's Working Well</h5>
                          <ul className="insight-list">
                            {(task.learned_patterns || []).slice(0, 3).map((pattern, idx) => (
                              <li key={idx} className="insight-item success">
                                {getHumanReadablePattern(pattern)}
                              </li>
                            ))}
                            {(task.learned_patterns?.length ?? 0) > 3 && (
                              <li className="insight-item more">
                                +{(task.learned_patterns?.length ?? 0) - 3} more patterns learned
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {(task.previous_issues?.length ?? 0) > 0 && (
                        <div className="insight-group">
                          <h5>🔧 Areas for Improvement</h5>
                          <ul className="insight-list">
                            {(task.previous_issues || []).slice(0, 2).map((issue, idx) => (
                              <li key={idx} className="insight-item warning">
                                {getHumanReadableIssue(issue)}
                              </li>
                            ))}
                            {(task.previous_issues?.length ?? 0) > 2 && (
                              <li className="insight-item more">
                                +{(task.previous_issues?.length ?? 0) - 2} more issues to address
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recommendations */}
                  {successRate < 70 && (
                    <div className="recommendations">
                      <h5>💡 Recommendations</h5>
                      <ul>
                        {task.previous_issues?.some((i: any) => i.type === 'missing_column' || i.column) && (
                          <li>Check that your input files have consistent column names</li>
                        )}
                        {task.previous_issues?.some((i: any) => i.type === 'date_parse_error') && (
                          <li>Standardize date formats across your data files</li>
                        )}
                        {successRate < 50 && (
                          <li>Consider updating the task framework with more specific rules</li>
                        )}
                        <li>Review recent failures to identify common patterns</li>
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="knowledge-base-loading">Loading Knowledge Base...</div>;
  }

  return (
    <div className="knowledge-base-container">
      <div className="kb-header">
        <h1>Knowledge Base</h1>
        <p>Manage accounting frameworks, domain rules, and company profile</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="kb-tabs">
        <button
          className={`tab ${activeTab === 'frameworks' ? 'active' : ''}`}
          onClick={() => { setActiveTab('frameworks'); setSearchQuery(''); setSelectedDomains([]); }}
        >
          Task Frameworks
        </button>
        <button
          className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => { setActiveTab('rules'); setSearchQuery(''); setSelectedRuleDomains([]); }}
        >
          Domain Rules
        </button>
        <button
          className={`tab ${activeTab === 'checks' ? 'active' : ''}`}
          onClick={() => { setActiveTab('checks'); setSearchQuery(''); }}
        >
          Safety Checks
        </button>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => { setActiveTab('profile'); setSearchQuery(''); }}
        >
          Company Profile
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => { setActiveTab('history'); setSearchQuery(''); }}
        >
          Task History
        </button>
      </div>

      <div className="kb-content">
        {activeTab === 'frameworks' && (
          <div className="frameworks-view">
            <div className="frameworks-content">
              <div className="config-sidebar">
                {renderConfigList('task')}
              </div>
              <div className="config-main">
                {renderConfigDetails()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="rules-view">
            <div className="rules-content">
              <div className="config-sidebar">
                {renderConfigList('domain')}
              </div>
              <div className="config-main">
                {renderConfigDetails()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'checks' && (
          <div className="checks-view">
            <div className="checks-content">
              <div className="config-sidebar">
                {renderConfigList('safety')}
              </div>
              <div className="config-main">
                {renderConfigDetails()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && renderTenantProfile()}

        {activeTab === 'history' && renderTaskHistory()}
      </div>

      {/* AI Config Creator Modal */}
      {showAICreator && (
        <div className="modal-overlay">
          <div className="modal-content">
            <AIConfigCreator
              configType={aiCreatorType}
              onConfigCreated={async (config) => {
                setShowAICreator(false);
                setSuccessMessage(`${aiCreatorType === 'task' ? 'Framework' : aiCreatorType === 'domain' ? 'Rules' : 'Checks'} created successfully with AI`);
                // Reload configs to show the new one
                await loadData();
                // Select the newly created config
                const newConfig = configs[aiCreatorType]?.find(c => c.name === config.name);
                if (newConfig) {
                  setSelectedConfig(newConfig);
                }
              }}
              onCancel={() => setShowAICreator(false)}
            />
          </div>
        </div>
      )}
      
      {/* Domain Rule Creator Modal */}
      {showDomainRuleCreator && (
        <div className="modal-overlay">
          <div className="modal-content domain-rule-creator-modal">
            <DomainRuleCreator
              onSuccess={async (config) => {
                setShowDomainRuleCreator(false);
                setSuccessMessage('Domain rules created successfully with AI');
                // Reload configs to show the new one
                await loadData();
                // Select the newly created config
                const newConfig = configs.domain?.find(c => c.name === config.name);
                if (newConfig) {
                  setSelectedConfig(newConfig);
                }
              }}
              onCancel={() => setShowDomainRuleCreator(false)}
              existingDomains={(configs.domain || []).map((rule: KnowledgeBaseConfig) => rule.config_name || rule.name || '')}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default KnowledgeBase;